/**
 * formatMatchDate.js
 * Convierte un Firestore Timestamp (UTC) a la fecha y hora local del usuario.
 * Funciona automáticamente para cualquier país: Colombia, Argentina, España, México, etc.
 */

/**
 * Formatea la fecha del partido en el timezone local del dispositivo.
 * @param {firebase.firestore.Timestamp} timestamp - kickoffTimestamp de Firestore
 * @param {object} options - opciones de formato
 * @returns {string} Ej: "Jue 11 Jun · 3:00 PM" (hora local del usuario)
 */
export function formatMatchLocal(timestamp, options = {}) {
  if (!timestamp) return '';

  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);

  const {
    showDate = true,
    showTime = true,
    showTimezone = false,
  } = options;

  const parts = [];

  if (showDate) {
    const dateStr = new Intl.DateTimeFormat(undefined, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    }).format(date);
    parts.push(dateStr);
  }

  if (showTime) {
    const timeStr = new Intl.DateTimeFormat(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(date);
    parts.push(timeStr);
  }

  if (showTimezone) {
    // Muestra la zona horaria del usuario, ej: "COT", "ART", "CET"
    const tz = new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' })
      .formatToParts(date)
      .find(p => p.type === 'timeZoneName')?.value || '';
    if (tz) parts.push(`(${tz})`);
  }

  return parts.join(' · ');
}

/**
 * Devuelve solo la hora local del partido.
 * Ej: "3:00 PM" en Colombia, "22:00" en España
 */
export function formatMatchTime(timestamp) {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
}

/**
 * Devuelve solo la fecha local del partido.
 * Ej: "Jue 11 Jun"
 */
export function formatMatchDate(timestamp) {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(date);
}

/**
 * Verifica si el partido ya está bloqueado (lockTimestamp pasó)
 * Usa UTC internamente, funciona igual en cualquier timezone.
 */
export function isMatchLocked(lockTimestamp) {
  if (!lockTimestamp) return false;
  const lock = lockTimestamp.toDate ? lockTimestamp.toDate() : new Date(lockTimestamp);
  return new Date() > lock;
}

/**
 * Devuelve cuánto tiempo falta para que se bloquee el partido.
 * Ej: "2h 15m" o "45m" o "bloqueado"
 */
export function timeUntilLock(lockTimestamp) {
  if (!lockTimestamp) return '';
  const lock = lockTimestamp.toDate ? lockTimestamp.toDate() : new Date(lockTimestamp);
  const diff = lock - new Date();
  if (diff <= 0) return 'bloqueado';
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}